/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

import { useState } from 'react';
import PropTypes from 'prop-types';
import { Avatar, Divider, IconButton, ListItemIcon, Menu, MenuItem, Box, Tooltip } from '@mui/material';
import {Logout, Settings} from '@mui/icons-material';
import {useDispatch} from 'react-redux';
import {logout} from '../../../../store/actions/auth';
import useAuthUser from '../../../../hooks/use-auth-user.hook';
import RouteEnum from "../../../../router/enums/route.enum";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";

function stringToColor(string) {
    let hash = 0;
    let i;

    for (i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';

    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }

    return color;
}

function stringAvatar(name) {
    return {
        sx: {
            bgcolor: stringToColor(name),
            width: 22,
            height: 22,
            fontSize: 11,
        },
        children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
    };
}

/**
 * @param {{ tooltipTitle?: string, tooltipPlacement?: string, tooltipSlotProps?: object, tooltipPopperProps?: object, disablePortal?: boolean, 'aria-label'?: string }} props
 */
const UserMenu = ({ tooltipTitle = undefined, tooltipPlacement = undefined, tooltipSlotProps = undefined, tooltipPopperProps = undefined, disablePortal = false, ...rest }) => {
    const dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const authUser = useAuthUser();
    const navigate = useNavigate();
    const {t} = useTranslation();

    const handleClick = (e) => {
        setAnchorEl(e.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        dispatch(logout());
    };

    if (!authUser) {
        return null;
    }

    const name = `${authUser.first_name} ${authUser.last_name}`;

    const profileButton = (
        <IconButton
            onClick={handleClick}
            size="small"
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            {...rest}
            sx={{
                '&:hover': {
                    backgroundColor: 'transparent',
                },
                ...rest.sx,
            }}
        >
            <Avatar {...stringAvatar(name)}/>
        </IconButton>
    );

    return (
        <>
            {tooltipTitle ? (
                <Tooltip title={tooltipTitle} placement={tooltipPlacement || 'bottom'} slotProps={tooltipSlotProps} PopperProps={tooltipPopperProps}>
                    {profileButton}
                </Tooltip>
            ) : (
                profileButton
            )}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                disablePortal={disablePortal}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: -1.5,
                        minWidth: 140,
                        '& .MuiAvatar-root': {
                            width: 20,
                            height: 20,
                            ml: -0.5,
                            mr: 1,
                            fontSize: 10,
                        },
                        '& .MuiMenuItem-root': {
                            py: 0.5,
                            fontSize: '0.813rem',
                            minHeight: 'auto',
                        },
                        '& .MuiListItemIcon-root': {
                            minWidth: 32,
                            '& svg': {
                                fontSize: '1rem',
                            },
                        },
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            bottom: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            transform: 'translateY(50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    },
                }}
                transformOrigin={{
                    horizontal: 'right',
                    vertical: 'bottom',
                }}
                anchorOrigin={{
                    horizontal: 'right',
                    vertical: 'top',
                }}
            >
                <MenuItem onClick={() => navigate(RouteEnum.EDIT_PROFILE)}>
                    <Avatar {...stringAvatar(name)} sx={{ width: 20, height: 20, fontSize: 10 }}/>
                    <Box sx={{ fontSize: '0.813rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100px' }}>
                        {name}
                    </Box>
                </MenuItem>
                <Divider/>
                <MenuItem onClick={() => navigate(RouteEnum.EDIT_PROFILE)}>
                    <ListItemIcon>
                        <Settings fontSize="small"/>
                    </ListItemIcon>
                    {t('profile.edit.button')}
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                        <Logout fontSize="small"/>
                    </ListItemIcon>
                    {t('auth.logout.button')}
                </MenuItem>
            </Menu>
        </>
    );
};

UserMenu.propTypes = {
  tooltipTitle: PropTypes.string,
  tooltipPlacement: PropTypes.string,
  tooltipSlotProps: PropTypes.object,
  tooltipPopperProps: PropTypes.object,
  disablePortal: PropTypes.bool,
};

export default UserMenu;
